/**
 * Acquisition-channel classification for first-touch attribution.
 *
 * `libs/attribution.ts` already stamps the raw referrer into the
 * `ae_attribution` cookie, and the server merges it onto the `signup`
 * analytics event. That records WHERE a signup came from but never groups
 * it — so "how many users did answer engines send us?" was unanswerable
 * without eyeballing raw referrer strings.
 *
 * This matters more than a normal channel breakdown. Generative engines
 * (ChatGPT, Perplexity, Gemini, Copilot, Claude) are a distinct acquisition
 * surface from classic search: they cite a handful of sources instead of
 * ranking ten links, and they mostly do not execute JavaScript, so the
 * content they can read is the pre-rendered HTML. Being able to see that
 * channel separately is what makes GEO work measurable rather than
 * faith-based.
 *
 * Shared (not client-local) so the server can re-derive the channel for
 * historical events when the rule set grows.
 */

/** Every acquisition channel, in display order. The closed set a metric label may take. */
export const ACQUISITION_CHANNELS = [
  "ai_chatgpt",
  "ai_perplexity",
  "ai_claude",
  "ai_gemini",
  "ai_copilot",
  "ai_other",
  "search_google",
  "search_bing",
  "search_other",
  "social",
  "referral",
  "direct",
] as const;

/** Coarse acquisition channels. `ai_*` values are the generative engines. */
export type AcquisitionChannel = (typeof ACQUISITION_CHANNELS)[number];

/**
 * Host suffix → channel. Order matters: the first match wins, so put the
 * specific engines ahead of the generic search hosts they may live under
 * (`gemini.google.com` before `google.`).
 */
const HOST_RULES: readonly (readonly [string, AcquisitionChannel])[] = [
  // ── Generative engines ────────────────────────────────────────────────
  ["chat.openai.com", "ai_chatgpt"],
  ["chatgpt.com", "ai_chatgpt"],
  ["openai.com", "ai_chatgpt"],
  ["perplexity.ai", "ai_perplexity"],
  ["claude.ai", "ai_claude"],
  ["anthropic.com", "ai_claude"],
  ["gemini.google.com", "ai_gemini"],
  ["bard.google.com", "ai_gemini"],
  ["copilot.microsoft.com", "ai_copilot"],
  ["edgeservices.bing.com", "ai_copilot"],
  ["you.com", "ai_other"],
  ["phind.com", "ai_other"],
  ["poe.com", "ai_other"],
  ["mistral.ai", "ai_other"],
  ["deepseek.com", "ai_other"],
  ["grok.com", "ai_other"],
  // ── Classic search ────────────────────────────────────────────────────
  ["google.", "search_google"],
  ["bing.com", "search_bing"],
  ["duckduckgo.com", "search_other"],
  ["search.yahoo.com", "search_other"],
  ["yandex.", "search_other"],
  ["ecosia.org", "search_other"],
  ["brave.com", "search_other"],
  ["baidu.com", "search_other"],
  // ── Social ────────────────────────────────────────────────────────────
  ["linkedin.com", "social"],
  ["lnkd.in", "social"],
  ["twitter.com", "social"],
  ["x.com", "social"],
  ["t.co", "social"],
  ["facebook.com", "social"],
  ["instagram.com", "social"],
  ["reddit.com", "social"],
  ["news.ycombinator.com", "social"],
  ["producthunt.com", "social"],
  ["youtube.com", "social"],
  ["t.me", "social"],
  ["whatsapp.com", "social"],
];

/**
 * `utm_source` values that identify an engine directly. ChatGPT appends
 * `?utm_source=chatgpt.com` to links it surfaces, and some clients strip the
 * `Referer` header entirely — in which case utm is the ONLY signal left.
 */
const UTM_RULES: readonly (readonly [string, AcquisitionChannel])[] = [
  ["chatgpt", "ai_chatgpt"],
  ["openai", "ai_chatgpt"],
  ["perplexity", "ai_perplexity"],
  ["claude", "ai_claude"],
  ["anthropic", "ai_claude"],
  ["gemini", "ai_gemini"],
  ["bard", "ai_gemini"],
  ["copilot", "ai_copilot"],
];

/** Extract a lowercase hostname from a referrer URL, or null if unparseable. */
const hostOf = (referrer: string): string | null => {
  try {
    return new URL(referrer).hostname.toLowerCase();
  } catch {
    return null;
  }
};

/**
 * Match a hostname against a rule needle on DOMAIN-LABEL boundaries.
 *
 * Substring matching would be wrong in a way that quietly corrupts the data:
 * `notgoogle.com` contains "google." and would land in search_google, and an
 * attacker-controlled `perplexity.ai.evil.com` could masquerade as an engine.
 *
 * A trailing dot means "TLD-agnostic" — `google.` matches google.com,
 * google.de and www.google.co.uk, but never notgoogle.com. Everything else is
 * an exact host or a subdomain of it.
 */
const matchesHost = (host: string, needle: string): boolean => {
  if (needle.endsWith(".")) {
    const base = needle.slice(0, -1);
    return host === base || host.startsWith(`${base}.`) || host.includes(`.${base}.`);
  }
  return host === needle || host.endsWith(`.${needle}`);
};

/**
 * Classify a visit into an acquisition channel.
 *
 * `utmSource` is checked FIRST: it is an explicit declaration by the sender,
 * whereas the referrer can be mangled by redirects or stripped by privacy
 * settings. A visit with neither signal is `direct`.
 *
 * @param referrer  `document.referrer` (external only — callers should not
 *                  pass same-origin navigations).
 * @param utmSource The `utm_source` query param, if present.
 */
export const classifyAcquisitionChannel = (
  referrer?: string | null,
  utmSource?: string | null,
): AcquisitionChannel => {
  const utm = utmSource?.trim().toLowerCase();
  if (utm) {
    for (const [needle, channel] of UTM_RULES) {
      if (utm.includes(needle)) return channel;
    }
  }

  const host = referrer ? hostOf(referrer) : null;
  if (host) {
    for (const [needle, channel] of HOST_RULES) {
      if (matchesHost(host, needle)) return channel;
    }
    return "referral";
  }

  // A utm_source with no engine match is still a deliberate campaign.
  return utm ? "referral" : "direct";
};

/** True for the generative-engine channels — the GEO surface. */
export const isAiChannel = (channel: AcquisitionChannel): boolean => channel.startsWith("ai_");
